import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Sun, Moon, LogOut, Menu, X, Leaf } from "lucide-react";
import { CultivWordmark, CultivMark } from "@/components/EdenWordmark";

/**
 * Cultiv app shell.
 * - Desktop: fixed dark-forest sidebar (240px) + cream main.
 * - Mobile: glass top bar + right-side drawer.
 * - "Areas" replaces "Gardens" — reinforcing ONE garden with eight areas.
 */
const NAV_PRIMARY = [
    { to: "/dashboard", label: "Today" },
    { to: "/blueprint", label: "Blueprint" },
    { to: "/tend", label: "Tend" },
];

const NAV_GARDEN = [
    { to: "/gardens", label: "Areas" },
    { to: "/journal", label: "Journal" },
    { to: "/patterns", label: "Patterns" },
    { to: "/prayer", label: "Prayer & Habits" },
    { to: "/scripture", label: "Scripture" },
    { to: "/timeline", label: "Growth" },
];

const NAV_ACCOUNT = [
    { to: "/season", label: "Season" },
    { to: "/membership", label: "Membership" },
    { to: "/modules", label: "Resources" },
];

const NavGroup = ({ title, items, onNavigate }) => (
    <div className="mb-6">
        {title && (
            <div className="px-4 mb-2 text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--eden-cream))]/35">
                {title}
            </div>
        )}
        <div className="flex flex-col gap-0.5">
            {items.map((item) => (
                <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onNavigate}
                    data-testid={`nav-${item.label.toLowerCase().replace(/ & /g, "-").replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                        `px-4 py-2 text-[13px] rounded-md transition-colors ease-cultiv duration-300 relative ${
                            isActive
                                ? "text-[hsl(var(--eden-cream))]"
                                : "text-[hsl(var(--eden-cream))]/55 hover:text-[hsl(var(--eden-cream))]"
                        }`
                    }
                >
                    {({ isActive }) => (
                        <span className="flex items-center">
                            {isActive && (
                                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 bg-[hsl(var(--eden-gold))]" />
                            )}
                            <span>{item.label}</span>
                        </span>
                    )}
                </NavLink>
            ))}
        </div>
    </div>
);

export const Layout = ({ children }) => {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const doLogout = async () => {
        await logout();
        window.location.href = "/";
    };

    return (
        <div className="min-h-screen bg-background text-foreground grain flex">
            {/* Desktop sidebar */}
            <aside
                className="hidden md:flex flex-col fixed inset-y-0 left-0 w-[248px] bg-[hsl(var(--eden-forest-deep))] text-[hsl(var(--eden-cream))] z-40"
                data-testid="app-sidebar"
            >
                <SidebarContent user={user} onNavigate={undefined} onLogout={doLogout} theme={theme} toggleTheme={toggle} />
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-40 glass border-b border-foreground/10" data-testid="mobile-topbar">
                <div className="px-6 py-4 flex items-center justify-between">
                    <NavLink to="/dashboard" className="flex items-center gap-2">
                        <Leaf className="w-4 h-4 text-[hsl(var(--eden-gold))]" strokeWidth={1.1} />
                        <span className="font-serif text-lg tracking-[0.32em]">CULTIV</span>
                    </NavLink>
                    <button onClick={() => setDrawerOpen(true)} aria-label="Open navigation" data-testid="mobile-menu-btn">
                        <Menu className="w-5 h-5" strokeWidth={1.5} />
                    </button>
                </div>
            </div>

            {/* Mobile drawer */}
            {drawerOpen && (
                <div className="md:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
                    <div className="flex-1 bg-[hsl(var(--eden-forest-deep))]/60" onClick={() => setDrawerOpen(false)} />
                    <div className="w-[288px] bg-[hsl(var(--eden-forest-deep))] text-[hsl(var(--eden-cream))] flex flex-col">
                        <div className="flex items-center justify-between p-6">
                            <span className="font-serif text-lg tracking-[0.32em]">CULTIV</span>
                            <button onClick={() => setDrawerOpen(false)} aria-label="Close">
                                <X className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                        </div>
                        <SidebarContent user={user} onNavigate={() => setDrawerOpen(false)} onLogout={doLogout} theme={theme} toggleTheme={toggle} compact />
                    </div>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 md:ml-[248px] relative">
                <main className="max-w-[1240px] mx-auto px-6 md:px-12 pt-24 md:pt-20 pb-24 md:pb-32 relative z-[2]">
                    {children}
                </main>
                <footer className="border-t border-foreground/10">
                    <div className="max-w-[1240px] mx-auto px-6 md:px-12 py-10 text-xs text-muted-foreground flex flex-col md:flex-row gap-2 md:gap-6 justify-between items-start md:items-center">
                        <div className="font-serif text-sm tracking-[0.32em] text-foreground uppercase">
                            Formed by Him · Filled by Him · Established by Him
                        </div>
                        <div className="italic text-[hsl(var(--eden-taupe))]">One garden. Faithfully cultivated.</div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

const SidebarContent = ({ user, onNavigate, onLogout, theme, toggleTheme, compact = false }) => (
    <>
        {!compact && (
            <NavLink to="/dashboard" className="pt-10 pb-8 flex flex-col items-center" data-testid="sidebar-logo">
                <CultivMark className="w-5 h-5" />
                <div className="font-serif text-xl tracking-[0.35em] mt-2">CULTIV</div>
            </NavLink>
        )}
        <div className="mx-6 h-px bg-[hsl(var(--eden-cream))]/12" />
        <div className="flex-1 px-3 py-6 overflow-y-auto">
            <NavGroup items={NAV_PRIMARY} onNavigate={onNavigate} />
            <NavGroup title="Your Garden" items={NAV_GARDEN} onNavigate={onNavigate} />
            <NavGroup title="Account" items={NAV_ACCOUNT} onNavigate={onNavigate} />
        </div>
        <div className="mx-6 h-px bg-[hsl(var(--eden-cream))]/12" />
        <div className="p-4">
            <div className="flex items-center gap-3 px-2 pb-3">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--eden-cream))]/10 flex items-center justify-center text-xs font-serif">
                    {user?.name?.slice(0, 1)?.toUpperCase() || "S"}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{user?.name}</div>
                    <div className="text-[11px] text-[hsl(var(--eden-cream))]/45 truncate capitalize">
                        {user?.subscription_tier === "premium" ? "Premium Steward" : "Steward"}
                    </div>
                </div>
            </div>
            <div className="flex items-center justify-between px-2">
                <button
                    onClick={toggleTheme}
                    data-testid="theme-toggle"
                    className="text-[hsl(var(--eden-cream))]/55 hover:text-[hsl(var(--eden-cream))] transition-colors p-2 rounded-md"
                    aria-label="Toggle theme"
                >
                    {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
                </button>
                <button
                    onClick={onLogout}
                    data-testid="logout-btn"
                    className="text-[hsl(var(--eden-cream))]/55 hover:text-[hsl(var(--eden-cream))] transition-colors text-[10px] uppercase tracking-[0.3em] flex items-center gap-2"
                >
                    <LogOut className="w-3.5 h-3.5" strokeWidth={1.5} /> Sign out
                </button>
            </div>
        </div>
    </>
);
